import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  FormControlLabel,
  Switch,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import OutlookCalendar from './OutlookCalendar';
import GoogleCalendar from './GoogleCalendar';
import api from '../../services/authService';

const Settings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({
    auto_transcribe_instant_meetings: false
  });

  useEffect(() => {
    document.title = 'Settings - EchoNote';
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/users/settings');
      setSettings({
        auto_transcribe_instant_meetings: response.data.auto_transcribe_instant_meetings || false
      });
      setLoading(false);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load settings');
      setLoading(false);
    }
  };

  const handleToggleAutoTranscribe = async (event) => {
    const newValue = event.target.checked;
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.put(
        '/users/settings',
        { auto_transcribe_instant_meetings: newValue }
      );
      
      setSettings(prev => ({
        ...prev,
        auto_transcribe_instant_meetings: newValue
      }));
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      
      <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
        API keys are now managed at the organization level. 
        {user?.orgRole === 'org_admin' ? (
          <> Visit the <strong>Organization Management</strong> page to configure API keys for your organization.</>
        ) : (
          <> Please contact your organization administrator to configure API keys.</>
        )}
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Transcription Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Transcription Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure your transcription preferences
        </Typography>

        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={settings.auto_transcribe_instant_meetings}
                onChange={handleToggleAutoTranscribe}
                disabled={saving}
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  Auto-transcribe instant meeting recordings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Automatically send instant meeting recordings to the transcription engine when uploaded
                </Typography>
              </Box>
            }
          />
        )}
      </Paper>

      {/* Outlook Calendar Integration */}
      <OutlookCalendar />

      {/* Google Calendar Integration */}
      <GoogleCalendar />
    </Box>
  );
};

export default Settings;
